import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import {
  deriveRideStatus,
  expectedJudgeCountForClass,
  expectedSectionCountForClass,
  filterAuthoritativeScoreSheets,
  isPanelConsensusTest,
  resolveScoreSheetTotals,
} from "score-common";
import {
  classes as classesCache,
  getAll,
  horses as horsesCache,
  people as peopleCache,
  rides as ridesCache,
  scoreSheets as scoreSheetsCache,
  tests as testsCache,
} from "../local/db";

// Best score (highest percentage) among the ride's sheets. Needs the test's
// movements to compute; null when the test is unresolved or there are no sheets.
//
// #1891 — resolveScoreSheetTotals (not calculateScoreSheet directly): a
// sheet already confirmed/completed under a since-corrected def must show
// its own frozen percentage here, matching every other surface, rather
// than recomputing against `test` (this hook's live catalog fetch) and
// silently disagreeing with the class grid, the reporting PDF, and FVD.
// An unconfirmed sheet has no frozen maxPoints yet, so this is a no-op for
// the normal in-progress case.
function bestPercentage(sheets, test) {
  if (!test || !sheets.length) return null;
  const best = sheets
    .map((s) => resolveScoreSheetTotals(s, test))
    .sort((a, b) => b.percentage - a.percentage)[0];
  return best?.percentage ?? null;
}

// Denormalize one ride for the ride-card: horse/rider names joined from the
// org's People/Horses, the derived lifecycle status, plus the best-available
// score among the ride's sheets. Manual rides carry their own display names.
function denormRide(ride, test, peopleById, horsesById, sheets, expectedJudgeCount, sectionJudge) {
  // #1763 — a panel-consensus ride (Test.panel.consensus) has ONE sheet
  // co-signed by the whole panel, so counting sheets against the class's
  // staffed judge positions would leave the badge stuck below FINAL for a ride
  // that is fully judged and signed. The Test is already in hand here (it is
  // what bestPercentage scores against), so the flag costs nothing to read.
  const consensus = isPanelConsensusTest(test);
  const status = deriveRideStatus(ride, sheets, expectedJudgeCount, { sectionJudge, consensus });
  const percentage = bestPercentage(sheets, test);
  if (ride.isManual) return { ...ride, status, percentage };
  const horse = horsesById[ride.horseId];
  const rider = peopleById[ride.riderId];
  return {
    ...ride,
    status,
    horseName: horse?.name ?? "—",
    riderName: rider ? `${rider.firstName} ${rider.lastName}` : "—",
    percentage,
  };
}

// Running order: scheduled rides by rideNumber, emergency manual rides last.
function assemble(rows) {
  const real = rows
    .filter((r) => !r.isManual)
    .sort((a, b) => (a.rideNumber ?? 0) - (b.rideNumber ?? 0));
  const manual = rows.filter((r) => r.isManual);
  return [...real, ...manual];
}

const byId = (list) => Object.fromEntries(list.map((x) => [x.id, x]));

// #2972 - a same-seat contender (#2815) must never feed a ride's status
// derivation or its best-percentage display: both readers below assume one
// sheet per judge position. Exported for direct unit testing (same reason
// useRidesByClassId's withStatus is exported).
export const groupSheetsByRide = (sheets) => {
  const map = {};
  for (const s of filterAuthoritativeScoreSheets(sheets)) (map[s.rideId] ??= []).push(s);
  return map;
};

/**
 * Rides for a class, denormalized for the ride list. Cache-first (#421): built
 * from the IndexedDB caches (rides indexed by classId, plus classes/tests/
 * people/horses/scoreSheets) and returned immediately, then revalidated from
 * score-api when online — the fetch writes every entity back into the cache so
 * the class works offline on the next visit.
 *
 * #203 / #380 / #386 — emergency manual rides live in the same rides cache
 * (isManual, written by addManualRide) and appear here without a network round
 * trip; their ScoreSheet still syncs to score-api via the outbox (#422).
 */
export function useRidesForClass(classId) {
  const api = useScoreApi();
  const { online } = useScoreContext();
  // #2821 — same warm-cache key ClassList's useClassesForShow uses (#2670):
  // without it, EVERY mount (including a same-session remount of a class
  // already resolved a moment earlier, e.g. leaving a ride's score screen and
  // coming straight back) starts `settled: false` and takes a real
  // readLocal() pass before RideList's skeleton gate (`!settled`) can clear,
  // even though this exact classId already has a good in-memory answer. A
  // remount with no cacheKey is a real skeleton flash, not just a stale-data
  // reflow — see cache-first-lists.cy.js's ride-list AC2 leg.
  const cacheKey = classId ? `ridesForClass:${classId}` : null;
  return useCachedResource(
    async () => {
      // #2196 — `undefined` here, not `{ klass: null, rides: [] }`: a caller
      // fed a classId that starts null and resolves later (SessionHeader gets
      // `klass?.id ?? null` from a still-loading parent) must be able to tell
      // "nothing known about this class yet" apart from "loaded, no rides".
      // useCachedResource's own `loading` flag can't carry that distinction
      // across a classId change (#2162: it never resets to true on a dep
      // change), so callers gate on `data === undefined` instead — see
      // SessionHeader's `rideDataKnown`. A real settle (local or remote)
      // never returns `undefined` itself, so the sentinel is unambiguous.
      if (!classId) return undefined;
      const klass = (await classesCache.get(classId)) ?? null;
      const rideRows = await ridesCache.byClass(classId);
      if (!klass && !rideRows.length) return undefined;
      // #435 — test is a property of the RIDE, not the class. Rides in one class
      // often ride DIFFERENT tests; resolve each ride's own test so the ride
      // card's score/percentage uses the right movements. (Never klass.testId.)
      const testsById = {};
      for (const tid of new Set(rideRows.map((r) => r.testId).filter(Boolean))) {
        testsById[tid] = (await testsCache.get(tid)) ?? null;
      }
      const peopleById = byId(await peopleCache.getAll());
      const horsesById = byId(await horsesCache.getAll());
      const sheetsByRide = groupSheetsByRide(await getAll("scoreSheets"));
      // FINAL needs the Class's judge count; null when the cached Class has no
      // judgeAssignments (offline before reconcile) — status degrades to
      // COMPLETED, never falsely FINAL.
      const expectedJudgeCount = expectedJudgeCountForClass(klass);
      const sectionJudge = expectedSectionCountForClass(klass) != null;
      const rows = rideRows.map((r) =>
        denormRide(
          r,
          testsById[r.testId] ?? null,
          peopleById,
          horsesById,
          sheetsByRide[r.id] ?? [],
          expectedJudgeCount,
          sectionJudge,
        ),
      );
      return { klass, rides: assemble(rows) };
    },
    async (signal) => {
      if (!classId) return undefined;

      // #3071 - every cache write below goes through `write`, which refuses once
      // this revalidation's signal has aborted (unmount, dep change, online
      // flip). A fetch can resolve in the same tick the abort fires, and the
      // `await`s after it keep running: without this an abandoned pass kept
      // writing rides/people/horses into the cache after the screen (or a
      // test's freshly reset database) had moved on, and left a partial write
      // with no class row. Throwing (not returning) so useCachedResource sees
      // its own teardown and discards the pass.
      const write = (fn) => {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        return fn();
      };

      let klassFailed = false;
      const [fetchedKlass, serverRides] = await Promise.all([
        api.classes
          .getClass(classId, { signal })
          .then(unwrap)
          .catch(() => {
            klassFailed = true;
            return null;
          }),
        api.rides.listRides(classId, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, { signal }).then(unwrap),
      ]);
      // #3071 - a failed getClass alone must not drop the class: RideList only
      // renders the emergency "Add Ride Manually" button when it has a class
      // (`settled && klass`), and that button is a hard live-show requirement,
      // including offline on a warm cache. Fall back to the cached class row.
      // No cached row either: keep returning null (never invent a class).
      let klass = fetchedKlass;
      if (klassFailed && !signal?.aborted) {
        klass = (await classesCache.get(classId)) ?? null;
        if (!klass) {
          console.warn(`useRidesForClass: getClass failed and no cached class row for ${classId}`);
        }
      }
      // #435 — resolve each ride's OWN test (ride.testId), not the class's. A
      // class can hold rides on different tests; using the class's single test
      // would render every ride card with the wrong movements/score.
      const testsById = {};
      await Promise.all(
        [...new Set(serverRides.map((r) => r.testId).filter(Boolean))].map(async (tid) => {
          const t = await api.tests.getTest(tid, { signal }).then(unwrap).catch(() => null);
          testsById[tid] = t;
          if (t) await write(() => testsCache.put(t));
        }),
      );
      const [people, horses] = await Promise.all([
        api.people.listPeople(undefined, { signal }).then(unwrap).catch(() => []),
        api.horses.listHorses({ signal }).then(unwrap).catch(() => []),
      ]);

      // Write everything through to the cache so the class is browsable offline.
      if (fetchedKlass) await write(() => classesCache.put(fetchedKlass));
      await write(() => ridesCache.putAll(serverRides));
      await write(() => peopleCache.putAll(people));
      await write(() => horsesCache.putAll(horses));

      const peopleById = byId(people);
      const horsesById = byId(horses);

      // #1961 — one batched call for the whole class's rides instead of one
      // listScoreSheets HTTP round trip per ride (the request-shape fix
      // docs/score/query-performance.md ranks this hook against).
      const rideIds = serverRides.map((r) => r.id);
      const allSheets = rideIds.length
        ? await api.scoreSheets
            .listScoreSheetsForRides({ rideIds }, { signal })
            .then(unwrap)
            .catch(() => [])
        : [];
      const sheetsByRide = groupSheetsByRide(allSheets);
      // #3008 — putAllFromServer, not a raw putAll: this batch prefetch
      // re-runs on every revalidation while this hook is mounted (mount,
      // reconnect, or an active poll) and used to clobber whatever ancestor
      // useScoreSheet.js's own per-ride fromApi() had just stamped on the
      // SAME scoreSheets rows, wiping stale_write auto-merge's tracked
      // baseline for a sheet a scribe had open. See #3008 HANDOFF 4.
      if (allSheets.length) await write(() => scoreSheetsCache.putAllFromServer(allSheets));

      const expectedJudgeCount = expectedJudgeCountForClass(klass);
      const sectionJudge = expectedSectionCountForClass(klass) != null;
      const rideRows = serverRides.map((r) =>
        denormRide(
          r,
          testsById[r.testId] ?? null,
          peopleById,
          horsesById,
          sheetsByRide[r.id] ?? [],
          expectedJudgeCount,
          sectionJudge,
        ),
      );
      // Emergency manual rides for this class live in the cache.
      const manualRows = (await ridesCache.byClass(classId))
        .filter((r) => r.isManual)
        .map((r) => ({ ...r, percentage: null }));

      return { klass, rides: assemble([...rideRows, ...manualRows]) };
    },
    [api, classId],
    online,
    cacheKey,
  );
}
